/**
 * useMarketingMaterials Hook
 * Manages marketing materials attached to documents
 * 
 * Features:
 * - Get suggestions based on line item products/categories
 * - List attached materials
 * - Attach/detach materials
 * - Track view counts
 * 
 * @version 1.0.0
 * @date December 2025
 */

import { useState, useCallback } from 'react';
import { authService } from '@/lib/authService';

// ============================================================================
// TYPES
// ============================================================================

export type MaterialType = 'brochure' | 'video' | 'html' | 'email' | 'presentation' | 'pdf' | 'image';
export type LinkSource = 'manual' | 'product' | 'category' | 'auto' | 'suggested';

export interface MarketingMaterial {
  id: number;
  title: string;
  description?: string;
  material_type: MaterialType;
  file_url?: string;
  thumbnail_url?: string;
  file_size?: number;
  file_format?: string;
  page_count?: number;
  duration?: number; // For videos
  share_token?: string;
  tags?: string[];
  view_count?: number;
  
  // Suggestion metadata
  matchedProducts?: number[];
  matchedCategories?: number[];
  matchReason?: 'product' | 'category';
}

export interface AttachedMaterial extends MarketingMaterial {
  attachment_id: string; // UUID from work_item_marketing_materials
  work_item_id: string;
  material_id: number;
  link_source: LinkSource;
  source_product_ids: number[];
  source_category_ids: number[];
  include_in_email: boolean;
  include_as_link: boolean;
  include_as_attachment: boolean;
  custom_note?: string;
  sort_order: number;
  attached_by_name?: string;
  first_viewed_at?: string;
  last_viewed_at?: string;
  download_count?: number;
  created_at: string;
}

export interface AttachMaterialOptions {
  material_id: number;
  link_source?: LinkSource;
  source_product_ids?: number[];
  source_category_ids?: number[];
  include_in_email?: boolean;
  include_as_link?: boolean;
  include_as_attachment?: boolean;
  custom_note?: string;
}

interface ApiResponse<T> {
  success?: boolean;
  data?: T;
  error?: string;
}

interface AttachActionResult {
  attached?: number;
  skipped?: number;
}

interface AttachedMaterialApiRow extends Omit<AttachedMaterial, 'id' | 'attachment_id' | 'source_product_ids' | 'source_category_ids'> {
  id: string;
  source_product_ids?: number[] | null;
  source_category_ids?: number[] | null;
}

function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

interface UseMarketingMaterialsReturn {
  // State
  suggestions: MarketingMaterial[];
  libraryMaterials: MarketingMaterial[];
  attachedMaterials: AttachedMaterial[];
  loading: boolean;
  error: string | null;
  
  // Actions
  getSuggestions: (productIds: number[], categoryIds: number[]) => Promise<MarketingMaterial[]>;
  getLibraryMaterials: (query?: string, limit?: number) => Promise<MarketingMaterial[]>;
  getAttachedMaterials: (workItemId: string) => Promise<AttachedMaterial[]>;
  attachMaterials: (workItemId: string, materials: AttachMaterialOptions[]) => Promise<{ attached: number; skipped: number }>;
  attachMaterialIds: (workItemId: string, materialIds: number[], linkSource?: LinkSource) => Promise<{ attached: number; skipped: number }>;
  detachMaterial: (workItemId: string, materialId: number) => Promise<boolean>;
  detachMaterialById: (attachmentId: string) => Promise<boolean>;
  updateMaterialSettings: (attachmentId: string, settings: Partial<AttachedMaterial>) => Promise<boolean>;
  clearError: () => void;
}

// ============================================================================
// HOOK
// ============================================================================

export function useMarketingMaterials(): UseMarketingMaterialsReturn {
  const [suggestions, setSuggestions] = useState<MarketingMaterial[]>([]);
  const [libraryMaterials, setLibraryMaterials] = useState<MarketingMaterial[]>([]);
  const [attachedMaterials, setAttachedMaterials] = useState<AttachedMaterial[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAuthHeaders = useCallback(() => {
    const tokens = authService.getTokens();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tokens?.access_token || ''}`
    };
  }, []);

  // Get suggestions based on products/categories in line items
  const getSuggestions = useCallback(async (
    productIds: number[],
    categoryIds: number[]
  ): Promise<MarketingMaterial[]> => {
    if (productIds.length === 0 && categoryIds.length === 0) {
      setSuggestions([]);
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        action: 'suggestions'
      });
      
      if (productIds.length > 0) {
        params.append('product_ids', productIds.join(','));
      }
      if (categoryIds.length > 0) {
        params.append('category_ids', categoryIds.join(','));
      }

      const response = await fetch(
        `/api/v1/work-items/marketing-materials?${params}`,
        { headers: getAuthHeaders() }
      );

      const result = await response.json() as ApiResponse<MarketingMaterial[]>;

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to get suggestions');
      }

      const nextSuggestions = result.data || [];
      setSuggestions(nextSuggestions);
      return nextSuggestions;
    } catch (err: unknown) {
      const message = getErrorMessage(err, 'Failed to get marketing material suggestions');
      setError(message);
      console.error('[useMarketingMaterials] getSuggestions error:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const getLibraryMaterials = useCallback(async (
    query = '',
    limit = 24,
  ): Promise<MarketingMaterial[]> => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        action: 'library',
        limit: String(limit),
      });

      if (query.trim()) {
        params.append('q', query.trim());
      }

      const response = await fetch(
        `/api/v1/work-items/marketing-materials?${params}`,
        { headers: getAuthHeaders() }
      );

      const result = await response.json() as ApiResponse<MarketingMaterial[]>;

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to load marketing material library');
      }

      const nextLibraryMaterials = result.data || [];
      setLibraryMaterials(nextLibraryMaterials);
      return nextLibraryMaterials;
    } catch (err: unknown) {
      const message = getErrorMessage(err, 'Failed to load marketing material library');
      setError(message);
      console.error('[useMarketingMaterials] getLibraryMaterials error:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Get materials attached to a document
  const getAttachedMaterials = useCallback(async (
    workItemId: string
  ): Promise<AttachedMaterial[]> => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        action: 'list',
        work_item_id: workItemId
      });

      const response = await fetch(
        `/api/v1/work-items/marketing-materials?${params}`,
        { headers: getAuthHeaders() }
      );

      const result = await response.json() as ApiResponse<AttachedMaterialApiRow[]>;

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to get attached materials');
      }

      // Map response to AttachedMaterial format
      const mapped: AttachedMaterial[] = (result.data || []).map((item) => ({
        ...item,
        id: item.material_id,
        attachment_id: item.id,
        source_product_ids: item.source_product_ids || [],
        source_category_ids: item.source_category_ids || []
      }));

      setAttachedMaterials(mapped);
      return mapped;
    } catch (err: unknown) {
      const message = getErrorMessage(err, 'Failed to get attached materials');
      setError(message);
      console.error('[useMarketingMaterials] getAttachedMaterials error:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Attach materials with detailed options
  const attachMaterials = useCallback(async (
    workItemId: string,
    materials: AttachMaterialOptions[]
  ): Promise<{ attached: number; skipped: number }> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/v1/work-items/marketing-materials', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          work_item_id: workItemId,
          materials
        })
      });

      const result = await response.json() as ApiResponse<AttachActionResult>;

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to attach materials');
      }

      // Refresh attached materials list
      await getAttachedMaterials(workItemId);

      return {
        attached: result.data?.attached || 0,
        skipped: result.data?.skipped || 0
      };
    } catch (err: unknown) {
      const message = getErrorMessage(err, 'Failed to attach materials');
      setError(message);
      console.error('[useMarketingMaterials] attachMaterials error:', err);
      return { attached: 0, skipped: 0 };
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, getAttachedMaterials]);

  // Simple attach by IDs
  const attachMaterialIds = useCallback(async (
    workItemId: string,
    materialIds: number[],
    linkSource: LinkSource = 'manual'
  ): Promise<{ attached: number; skipped: number }> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/v1/work-items/marketing-materials', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          work_item_id: workItemId,
          material_ids: materialIds,
          link_source: linkSource
        })
      });

      const result = await response.json() as ApiResponse<AttachActionResult>;

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to attach materials');
      }

      // Refresh attached materials list
      await getAttachedMaterials(workItemId);

      return {
        attached: result.data?.attached || 0,
        skipped: result.data?.skipped || 0
      };
    } catch (err: unknown) {
      const message = getErrorMessage(err, 'Failed to attach materials');
      setError(message);
      console.error('[useMarketingMaterials] attachMaterialIds error:', err);
      return { attached: 0, skipped: 0 };
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, getAttachedMaterials]);

  // Detach material by work_item_id + material_id
  const detachMaterial = useCallback(async (
    workItemId: string,
    materialId: number
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        work_item_id: workItemId,
        material_id: materialId.toString()
      });

      const response = await fetch(
        `/api/v1/work-items/marketing-materials?${params}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders()
        }
      );

      const result = await response.json() as ApiResponse<null>;

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to detach material');
      }

      // Update local state
      setAttachedMaterials(prev => 
        prev.filter(m => !(m.work_item_id === workItemId && m.material_id === materialId))
      );

      return true;
    } catch (err: unknown) {
      const message = getErrorMessage(err, 'Failed to detach material');
      setError(message);
      console.error('[useMarketingMaterials] detachMaterial error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Detach by attachment ID
  const detachMaterialById = useCallback(async (
    attachmentId: string
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ id: attachmentId });

      const response = await fetch(
        `/api/v1/work-items/marketing-materials?${params}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders()
        }
      );

      const result = await response.json() as ApiResponse<null>;

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to detach material');
      }

      // Update local state
      setAttachedMaterials(prev => 
        prev.filter(m => m.attachment_id !== attachmentId)
      );

      return true;
    } catch (err: unknown) {
      const message = getErrorMessage(err, 'Failed to detach material');
      setError(message);
      console.error('[useMarketingMaterials] detachMaterialById error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Update material attachment settings
  const updateMaterialSettings = useCallback(async (
    attachmentId: string,
    settings: Partial<AttachedMaterial>
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/v1/work-items/marketing-materials', {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          id: attachmentId,
          ...settings
        })
      });

      const result = await response.json() as ApiResponse<null>;

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to update material');
      }

      // Update local state
      setAttachedMaterials(prev => 
        prev.map(m => m.attachment_id === attachmentId ? { ...m, ...settings } : m)
      );

      return true;
    } catch (err: unknown) {
      const message = getErrorMessage(err, 'Failed to update material');
      setError(message);
      console.error('[useMarketingMaterials] updateMaterialSettings error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    suggestions,
    libraryMaterials,
    attachedMaterials,
    loading,
    error,
    getSuggestions,
    getLibraryMaterials,
    getAttachedMaterials,
    attachMaterials,
    attachMaterialIds,
    detachMaterial,
    detachMaterialById,
    updateMaterialSettings,
    clearError
  };
}

export default useMarketingMaterials;
